import { clearRecentSearches } from "client/api/recent-searches-service";
import { useDataContext } from "client/context/DataContext";
import { isValidASTerm } from "client/utils/helpers";
import React, { useMemo, useState } from "react";
import { SUGGESTED_OR_RECENT_SEARCHES_LIMIT } from "../../utils/constants";
import SectionTitle from "../section/section-title";
import ProposedSearch from "./proposed-search";
import {
  ClearAll,
  StyledClearButton,
  StyledProposalsContainer,
  StyledProposalsWrapper,
  StyledRecentSearchesSectionNameWrapper,
} from "./searches.styles";

const Searches = () => {
  const { searches, searchedTerm, isRecommendations, recommendationsData } =
    useDataContext();
  const [allowDisplayRecentSearches, setAllowDisplayRecentSearches] =
    useState<boolean>(true);

  const isRecentSearch = useMemo(
    () =>
      Boolean(
        searches.recent?.length &&
          !isValidASTerm(searchedTerm) &&
          allowDisplayRecentSearches,
      ),
    [searchedTerm, searches.recent?.length, allowDisplayRecentSearches],
  );
  const proposedTerms = useMemo(() => {
    const terms = isRecentSearch ? searches.recent : searches.suggested;
    if (!terms?.length) return [];

    return terms
      .filter((term) => term)
      .slice(0, SUGGESTED_OR_RECENT_SEARCHES_LIMIT);
  }, [isRecentSearch, searches.recent, searches.suggested]);

  const prefix = !isRecentSearch ? "suggested" : "recent";

  const label = !isRecentSearch ? "Suggested searches" : "Your recent searches";

  const recsPodId = recommendationsData?.popularSuggestions?.podId;
  const recsResultId = recommendationsData?.popularSuggestions?.resultId;
  const recsNumResults = recommendationsData?.popularSuggestions?.numResults;
  const suggestionStrategyIds =
    recommendationsData?.popularSuggestions?.strategyIds || [];

  const cnstrcData = useMemo(() => {
    const data: Record<string, boolean | string | number> = {};
    if (isRecommendations && !isRecentSearch) {
      data["data-cnstrc-recommendations"] = true;
      if (recsPodId) data["data-cnstrc-recommendations-pod-id"] = recsPodId;
      if (recsResultId) data["data-cnstrc-result-id"] = recsResultId;
      if (recsNumResults) data["data-cnstrc-num-results"] = recsNumResults;
    }
    return data;
  }, [
    isRecommendations,
    isRecentSearch,
    recsPodId,
    recsResultId,
    recsNumResults,
  ]);

  if (!proposedTerms.length) return null;
  return (
    <StyledProposalsContainer
      data-testid={`${prefix}-searches-container`}
      {...cnstrcData}
    >
      {!isRecentSearch ? (
        <SectionTitle
          dataTestId={`${prefix}-searches-container-label`}
          id={`${prefix}-searches-container-label`}
        >
          {label}
        </SectionTitle>
      ) : (
        <StyledRecentSearchesSectionNameWrapper>
          <SectionTitle
            dataTestId={`${prefix}-searches-container-label`}
            id={`${prefix}-searches-container-label`}
          >
            {label}
          </SectionTitle>
          <StyledClearButton onClick={clearRecentSearches}>
            <ClearAll tag="span" type="paragraph" size="m2">
              Clear all
            </ClearAll>
          </StyledClearButton>
        </StyledRecentSearchesSectionNameWrapper>
      )}
      <StyledProposalsWrapper
        data-testid={`${prefix}-searches-container-list`}
        role="list"
        aria-labelledby={`${prefix}-searches-container-label`}
      >
        {proposedTerms.map((term, index) => (
          <ProposedSearch
            key={index}
            term={term}
            isRecentSearch={isRecentSearch}
            searchedTerm={searchedTerm}
            isRecommendations={isRecommendations}
            setAllowDisplayRecentSearches={setAllowDisplayRecentSearches}
            strategyId={suggestionStrategyIds[index]}
          />
        ))}
      </StyledProposalsWrapper>
    </StyledProposalsContainer>
  );
};

export default Searches;
